import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { CellAllowedDays } from 'src/features/memberships/components/table/cells/CellAllowedDays';
import { badgeAllowedDaysOptions, END_FULL_DAY, START_FULL_DAY } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking';
import { useListPrepaidPlans } from 'src/features/subscriptions/api/useListPrepaidPlans';
import CellSubscriptionPlanActions from 'src/features/subscriptions/components/CellSubscriptionPlanActions';
import SubscriptionPlanFormModal from 'src/features/subscriptions/components/forms/SubscriptionPlanFormModal';
import { DURATION_MAP, LANGUAGE_MAP, type PrepaidPlan } from 'src/features/subscriptions/types';
import subscriptionMessages from 'src/features/subscriptions/utils/messages';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import {
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellPrice } from 'src/registry/components/table/cells/CellPrice';
import { useTitleStore } from 'src/stores/useTitleStore';
import { getTooltipCell } from 'src/utils/getTooltipCell';
import { generalPriceMessages } from 'src/utils/messages';

function SubscriptionPlansTable() {
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();

    const {
        data: prepaidPlansData,
        isLoading,
        isError,
        refetch,
    } = useListPrepaidPlans({ parkingId: parkingId || '', config: { enabled: !!parkingId } });

    const [plansModalData, setPlansModalData] = React.useState<{ selectedPlan: null | PrepaidPlan; isDelete: boolean }>(
        {
            selectedPlan: null,
            isDelete: false,
        },
    );

    const { data } = useGetParking({ parkingId: parkingId || '', config: { enabled: !!parkingId } });

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'parking-subscription-plans' });
    const columnHelper = createColumnHelper<PrepaidPlan>();

    const columns = [
        columnHelper.accessor('plan_name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(subscriptionMessages.name),
            },
        }),
        columnHelper.accessor('duration.unit', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(subscriptionMessages.cycleType),
            },
            cell: ({ row }) => {
                if (!row.original.duration) return;
                return (
                    <span className="whitespace-nowrap">
                        {DURATION_MAP[row.original.duration.unit]
                            ? formatMessage(DURATION_MAP[row.original.duration.unit].type)
                            : ''}
                    </span>
                );
            },
        }),
        columnHelper.accessor('duration.value', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(subscriptionMessages.cycleCount),
            },
            cell: ({ row }) => {
                if (!row.original.duration) return '';
                return (
                    <span className="whitespace-nowrap">
                        {DURATION_MAP[row.original.duration.unit]
                            ? formatMessage(DURATION_MAP[row.original.duration.unit].count, {
                                  value: row.original.duration?.value,
                              })
                            : ''}
                    </span>
                );
            },
        }),
        columnHelper.accessor('dueAmountWithTax', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(generalPriceMessages.price),
            },
            cell: CellPrice,
        }),
        columnHelper.display({
            header: formatMessage(membershipMessages.allowedHours),
            id: 'allowed_hours',
            cell: ({ row }) => {
                if (
                    row.original.allowedHours.fromHour === START_FULL_DAY &&
                    row.original.allowedHours.toHour === END_FULL_DAY
                )
                    return formatMessage(membershipMessages.fullDay);
                return (
                    <div className="flex flex-wrap">
                        <span className="whitespace-nowrap">{row.original.allowedHours.fromHour}</span>
                        <span> - </span>
                        <span className="whitespace-nowrap">{row.original.allowedHours.toHour}</span>
                    </div>
                );
            },
        }),
        columnHelper.accessor('allowedWeekDays', {
            header: DataTableColumnHeader,
            cell: CellAllowedDays,
            meta: {
                title: formatMessage(membershipMessages.allowedDays),
                badgeOptions: badgeAllowedDaysOptions,
            },
        }),
        columnHelper.accessor('endAtMidnight', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(subscriptionMessages.endType),
            },
            cell: ({ row }) => {
                if (!row.original) return '';
                return (
                    <span className="whitespace-nowrap">
                        {formatMessage(
                            row.original.endAtMidnight ? subscriptionMessages.endDay : subscriptionMessages.endCycle,
                        )}
                    </span>
                );
            },
        }),
        columnHelper.accessor('languages', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(subscriptionMessages.language),
            },
            cell: ({ row }) => {
                if (!row.original.languages) return '';
                const message = row.original.languages.reduce((previousValue, language, index) => {
                    if (!LANGUAGE_MAP[language]) return previousValue;
                    return `${previousValue}${index ? ', ' : ''}${formatMessage(LANGUAGE_MAP[language])}`;
                }, '');
                return <span className="whitespace-nowrap">{message}</span>;
            },
        }),
        columnHelper.accessor('description', {
            header: DataTableColumnHeader,
            cell: ({ row }) => getTooltipCell({ value: row.original.description ? row.original.description : '' }),
            meta: {
                title: formatMessage(subscriptionMessages.description),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellSubscriptionPlanActions
                    deleteCallback={() => setPlansModalData({ selectedPlan: props.row.original, isDelete: true })}
                    editCallback={() => setPlansModalData({ selectedPlan: props.row.original, isDelete: false })}
                    tooltipMessage={subscriptionMessages.subscriptionPlan}
                    {...props}
                />
            ),
        }),
    ];

    const resetPlansData = () => setPlansModalData({ selectedPlan: null, isDelete: false });

    return (
        <>
            {plansModalData.selectedPlan && (
                <SubscriptionPlanFormModal
                    selectedSubscriptionPlan={plansModalData.selectedPlan}
                    isDialogOpen={!!plansModalData.selectedPlan}
                    isDelete={plansModalData.isDelete}
                    removeTrigger
                    onCloseDialog={resetPlansData}
                    onSubmit={() => {
                        resetPlansData();
                        refetch();
                    }}
                />
            )}
            <DataTable
                columns={columns}
                data={prepaidPlansData}
                pagination={pagination}
                sorting={sorting}
                search={search}
                semantics={{
                    title: formatMessage(subscriptionMessages.subscriptionPlans),
                    entryName: formatMessage(subscriptionMessages.plan),
                    entryNames: formatMessage(subscriptionMessages.plans),
                }}
                isError={isError}
                isLoading={isLoading}
                refetch={refetch}
                components={{
                    newEntry: SubscriptionPlanFormModal,
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
        </>
    );
}

export default SubscriptionPlansTable;
